<template>
  <div
    class="contact-form"
    :class="{ 'start-page': currentPage === 'start' }"
  >
    <!-- Page: Start -->
    <div v-if="currentPage === 'start'">
      <div class="top">
        <legend>Discover Quiz</legend>
        <button
          class="close-btn"
          type="button"
          @click="handleClose"
        >
          <font-awesome-icon :icon="icon" />
        </button>
      </div>
      <div class="hero">
        <div
          class="hero-text"
          aria-hidden="true"
        >
          <span
            v-for="row in 5"
            :key="row"
          >Football Season</span>
        </div>
        <img
          alt="Students holding Powered by Purpose signs"
          :src="studentsSrc"
        >
      </div>
      <h1>PLAY NOW!</h1>
      <h2>How Well Do You Know Retool Your School?</h2>
      <p>5 quick questions. Get points. See how you stack up.</p>
      <div class="center-button">
        <button
          class="btn btn-invert"
          type="button"
          @click="handleClose"
        >
          MAYBE LATER
        </button>
        <button
          class="btn"
          type="button"
          @click="startQuiz"
        >
          START NOW!
        </button>
      </div>
    </div>

    <!-- Page: Questions -->
    <div v-if="currentPage === 'question' && currentQuestion">
      <div
        v-if="answerResult === null"
        class="top"
      >
        <div class="top-title">
          <legend>Question {{ questionNumber }} of {{ QUESTIONS_PER_QUIZ }}</legend>
          <div
            class="progress"
            aria-hidden="true"
          >
            <span
              v-for="step in QUESTIONS_PER_QUIZ"
              :key="step"
              class="progress-box"
              :class="{ active: step <= questionNumber }"
            />
          </div>
        </div>
        <div class="points-get">
          <p>+{{ currentQuestion.points }} pt</p>
        </div>
        <button
          class="close-btn"
          type="button"
          @click="handleClose"
        >
          <font-awesome-icon :icon="icon" />
        </button>
      </div>
      <fieldset v-if="answerResult === null">
        <h2>{{ currentQuestion.question }}</h2>
        <div class="answers">
          <div
            v-for="(option, index) in currentQuestion.options"
            :key="`${currentQuestion.id}_${index}`"
          >
            <input
              :id="`${currentQuestion.id}_${index}`"
              v-model="selectedAnswer"
              type="radio"
              :value="option.value"
              class="answer"
              :name="currentQuestion.id"
              @change="resetInactivityTimer"
            >
            <label
              class="btn btn-invert"
              :class="{ 'has-image': option.image }"
              :for="`${currentQuestion.id}_${index}`"
            >
              <img
                v-if="option.image"
                :src="option.image"
                alt=""
              >
              <span>{{ option.label }}</span>
            </label>
          </div>
        </div>
        <div class="center-button next-button">
          <button
            class="btn"
            type="button"
            :disabled="selectedAnswer === null"
            @click="handleAnswer"
          >
            Next
          </button>
        </div>
      </fieldset>
      <div
        v-else
        :class="answerResult === 'correct' ? 'correct-message' : 'incorrect-message'"
      >
        <div class="hero hero-result">
          <div
            class="hero-text"
            aria-hidden="true"
          >
            <span
              v-for="row in 3"
              :key="row"
            >Points Earned</span>
          </div>
          <img
            :alt="answerResult === 'correct' ? 'Person cheering with fists raised' : 'Person shrugging'"
            :src="answerResult === 'correct' ? winPointsSrc : noPointsSrc"
          >
        </div>
        <div v-if="answerResult === 'correct'">
          <h1><span>+</span>{{ displayedPoints }}</h1>
          <h2>You are a rockstar!</h2>
        </div>
        <div v-else>
          <h1><span>+</span>0</h1>
          <h2>Try again!</h2>
        </div>
        <!-- <p class="score">
          Total Score: {{ score }}
        </p> -->
        <div class="center-button">
          <button
            class="btn"
            type="button"
            @click="nextQuestion"
          >
            {{ isLastQuestion ? 'See My Score' : 'NEXT QUESTION' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Page: Email -->
    <div
      v-if="currentPage === 'email'"
      class="email-page"
    >
      <div class="hero hero-result">
        <div
          class="hero-text"
          aria-hidden="true"
        >
          <span
            v-for="row in 3"
            :key="row"
          >Quiz Complete</span>
        </div>
        <img
          alt="Quiz complete"
          :src="resultsSrc"
        >
      </div>
      <form
        novalidate
        @submit.prevent="submitEmail"
      >
        <h2>NICE JOB!</h2>
        <p>Enter Your Email to See Your Results</p>
        <div class="input-holder">
          <label
            for="quiz-discover-email"
            class="visually-hidden"
          >Email</label>
          <input
            id="quiz-discover-email"
            v-model.trim="email"
            type="email"
            autocomplete="email"
            placeholder="Enter your email"
            required
            :class="{ error: emailError }"
            :aria-invalid="emailError"
            aria-describedby="quiz-discover-email-error"
            @input="emailError = false; resetInactivityTimer()"
          >
          <p
            v-if="emailError"
            id="quiz-discover-email-error"
            class="error-message"
          >
            Please enter a valid email address.
          </p>
        </div>
        <div class="center-button">
          <button
            class="btn"
            type="submit"
            :disabled="submitting"
          >
            {{ submitting ? 'Submitting...' : 'See My Score' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Page: Results -->
    <div
      v-if="currentPage === 'results'"
      class="results-message"
    >
      <div class="hero hero-result">
        <div
          class="hero-text"
          aria-hidden="true"
        >
          <span
            v-for="row in 3"
            :key="row"
          >Quiz Complete</span>
        </div>
        <img
          alt="Quiz complete"
          :src="resultsSrc"
        >
        <button
          class="close-btn"
          type="button"
          aria-label="Close"
          @click="handleClose"
        >
          <font-awesome-icon :icon="icon" />
        </button>
      </div>
      <div class="final-score">
        <h2>Nice Work!</h2>
        <h1>{{ score }}</h1>
        <p>out of {{ maxScore }} points</p>
      </div>
      <!-- TODO: replace placeholder with the real QR code image -->
      <!-- <div class="qr-placeholder">
        QR Code
      </div> -->
      <!-- TODO: wire up Learn More action -->
      <div class="center-button">
        <!-- <button
          class="btn btn-outline"
          type="button"
        >
          LEARN MORE
        </button> -->
        <button
          class="btn btn-solid"
          type="button"
          :aria-expanded="showShareOptions"
          @click="onShareClick"
        >
          SHARE MY SCORE
        </button>
      </div>
      <div
        v-if="showShareOptions"
        class="share-options"
      >
        <button
          v-for="network in SHARE_NETWORKS"
          :key="network.name"
          class="share-option"
          type="button"
          :aria-label="`Share on ${network.label}`"
          @click="shareScore(network)"
        >
          <font-awesome-icon :icon="['fab', network.icon]" />
        </button>
      </div>
    </div>

    <!-- Page: Timed Out -->
    <div
      v-if="currentPage === 'timeout'"
      class="wrong-answer-page"
    >
      <div class="top">
        <legend>Oops...</legend>
        <button
          class="close-btn"
          type="button"
          @click="handleClose"
        >
          <font-awesome-icon :icon="icon" />
        </button>
      </div>
      <h1>Hey You've Timed Out</h1>
      <p>Please Try Again</p>
      <div class="center-button">
        <button
          class="btn"
          type="button"
          @click="startQuiz"
        >
          Play Now
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import noPoints1Src from '@/assets/imgs/no-points-1.gif';
import noPoints2Src from '@/assets/imgs/no-points-2.gif';
import noPoints3Src from '@/assets/imgs/no-points-3.gif';
import noPoints4Src from '@/assets/imgs/no-points-4.gif';
import resultsSrc from '@/assets/imgs/results.gif';
import studentsSrc from '@/assets/imgs/students.png';
import pointsEarned1Src from '@/assets/imgs/points-earned-1.gif';
import pointsEarned2Src from '@/assets/imgs/points-earned-2.gif';
import pointsEarned3Src from '@/assets/imgs/points-earned-3.gif';
import pointsEarned4Src from '@/assets/imgs/points-earned-4.gif';
import pointsEarned5Src from '@/assets/imgs/points-earned-5.gif';

const FORM_ID = 8;
// TODO: confirm this matches the Email field ID on Gravity Forms form 8.
const EMAIL_FIELD_ID = 'input_35';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const QUESTIONS_PER_QUIZ = 5;
const WIN_POINTS_IMAGES = [pointsEarned1Src, pointsEarned2Src, pointsEarned3Src, pointsEarned4Src, pointsEarned5Src];
const NO_POINTS_IMAGES = [noPoints1Src, noPoints2Src, noPoints3Src, noPoints4Src];
const COUNT_UP_DURATION_MS = 1000;
const INACTIVITY_TIMEOUT_MS = 7000000; // 100 minutes
const SHARE_PATH = '/share/quiz';
const SHARE_HASHTAG = 'RetoolYourSchool';
// Facebook and LinkedIn ignore pre-filled text and build the post from the share page's
// Open Graph tags (netlify/functions/quiz-share.mts); the others also take the score as text.
const SHARE_NETWORKS = [
  {
    name: 'facebook',
    label: 'Facebook',
    icon: 'facebook-f',
    buildUrl: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${url}&hashtag=${encodeURIComponent(`#${SHARE_HASHTAG}`)}`
  },
  {
    name: 'linkedin',
    label: 'LinkedIn',
    icon: 'linkedin-in',
    buildUrl: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
  },
  {
    name: 'twitter',
    label: 'X (Twitter)',
    icon: 'x-twitter',
    buildUrl: ({ url, text }) => `https://twitter.com/intent/tweet?url=${url}&text=${text}&hashtags=${SHARE_HASHTAG}`
  },
  {
    name: 'pinterest',
    label: 'Pinterest',
    icon: 'pinterest-p',
    buildUrl: ({ url, text, image }) => `https://www.pinterest.com/pin/create/button/?url=${url}&media=${image}&description=${text}`
  },
  {
    name: 'reddit',
    label: 'Reddit',
    icon: 'reddit-alien',
    buildUrl: ({ url, text }) => `https://www.reddit.com/submit?url=${url}&title=${text}`
  }
];

// `id` must match the Gravity Forms field ID on form 8, and each option `value` must match
// that field's choice value. `correctAnswer: null` means any answer earns the points
// (player's choice). Options can also take an optional `image` (import it from
// '@/assets/imgs/...' like the images above), which is shown above the label text.
const option = (value, label) => ({ value, label: `${value.toUpperCase()}. ${label}` });

const QUESTIONS = [
  {
    id: 'input_1',
    question: 'How many times has Jackson State University won a Retool Your School grant?',
    options: [option('a', 'Nine Times'), option('b', 'Eight Times'), option('c', 'Seven Times'), option('d', 'Six Times')],
    correctAnswer: 'b',
    points: 10
  },
  {
    id: 'input_4',
    question: 'St. Philip\'s College won a grant in what year?',
    options: [option('a', '2020'), option('b', '2014'), option('c', '2012'), option('d', '2021')],
    correctAnswer: 'd',
    points: 10
  },
  // TODO: needs the school logo image (with the school name blurred out) before it can be enabled.
  // {
  //   id: 'input_6',
  //   question: 'Guess the school by the logo?',
  //   options: [option('a', 'Shorter College'), option('b', 'Savannah State College'), option('c', 'Lane College'), option('d', 'Rust College')],
  //   correctAnswer: 'c',
  //   points: 10
  // },
  {
    id: 'input_8',
    question: 'Which mascot belongs to Delaware State University?',
    options: [option('a', 'Lion'), option('b', 'Bear'), option('c', 'Tiger'), option('d', 'Hornet')],
    correctAnswer: 'd',
    points: 10
  },
  // {
  //   id: 'input_10',
  //   question: 'What school has received a Retool Your School grant dating back to 2010?',
  //   options: [option('a', 'Virginia Union University'), option('b', 'Morris Brown College'), option('c', 'Southern Connecticut State University'), option('d', 'Talladega College')],
  //   correctAnswer: 'a',
  //   points: 10
  // },
  {
    id: 'input_12',
    question: 'What is your favorite way to show off your school pride?',
    options: [option('a', 'A classic school T-shirt'), option('b', 'A head-to-toe school-color look'), option('c', 'Face paint and accessories'), option('d', 'A vintage alumni sweatshirt')],
    correctAnswer: null,
    points: 20
  },
  {
    id: 'input_14',
    question: 'How many schools are represented in this year\'s Retool Your School program?',
    options: [option('a', '34'), option('b', '20'), option('c', '42'), option('d', '24')],
    correctAnswer: 'd',
    points: 10
  },
  // {
  //   id: 'input_18',
  //   question: 'You just got to campus for game day. What\'s the first thing you\'re doing?',
  //   options: [option('a', 'Finding the tailgate'), option('b', 'Finding the student section'), option('c', 'Taking pictures in your school gear'), option('d', 'All of the above')],
  //   correctAnswer: 'd',
  //   points: 20
  // },
  // {
  //   id: 'input_19',
  //   question: 'What makes a college rivalry game feel different?',
  //   options: [option('a', 'School pride is turned ALL the way up'), option('b', 'Nobody cares about the outcome'), option('c', 'The stadium is empty'), option('d', 'There are no fans')],
  //   correctAnswer: 'a',
  //   points: 20
  // },
  {
    id: 'input_20',
    question: 'Which of these schools has a Retool Your School record spanning both the 2010s and the 2020s?',
    options: [option('a', 'Fisk University'), option('b', 'Hampton University'), option('c', 'Southern University'), option('d', 'All of the above')],
    correctAnswer: 'd',
    points: 10
  },
  {
    id: 'input_21',
    question: 'Which one of these schools received an Innovation Lounge?',
    options: [option('a', 'Fort Lewis College'), option('b', 'North Carolina Central University'), option('c', 'National Louis University'), option('d', 'Victoria College')],
    correctAnswer: 'c',
    points: 10
  },
  {
    id: 'input_22',
    question: 'What school received both a Community Project Grant and an Innovation Lounge Grant in 2024?',
    options: [option('a', 'Jarvis Christian University'), option('b', 'Jackson State University'), option('c', 'Lincoln University of Missouri'), option('d', 'Norfolk State University')],
    correctAnswer: 'a',
    points: 10
  },
  // {
  //   id: 'input_36',
  //   question: 'Which two teams compete in the annual Magic City Classic?',
  //   options: [option('a', 'Alabama A&M University and Alabama State University'), option('b', 'Howard University and Hampton University'), option('c', 'Florida A&M University and Bethune-Cookman University'), option('d', 'Morehouse College and Clark Atlanta University')],
  //   correctAnswer: 'a',
  //   points: 10
  // },
  // {
  //   id: 'input_37',
  //   question: 'Which university encourages fans to create a sea of maroon during its annual Maroon Out football game?',
  //   options: [option('a', 'Texas A&M University'), option('b', 'Adelphi University'), option('c', 'Southern Connecticut State University'), option('d', 'Fort Lewis College')],
  //   correctAnswer: 'a',
  //   points: 10
  // },
  {
    id: 'input_38',
    question: 'Which 2026 Campus Improvement Grant recipient is located in New Haven, Connecticut?',
    options: [option('a', 'Victoria College'), option('b', 'Southern Connecticut State University'), option('c', 'Carroll College'), option('d', 'Harris-Stowe State University')],
    correctAnswer: 'b',
    points: 10
  },
  // {
  //   id: 'input_39',
  //   question: 'What is a rivalry game in college football?',
  //   options: [option('a', 'A game between teams with a longstanding competitive history'), option('b', 'A game played only during the playoffs'), option('c', 'A game between professional and college teams'), option('d', 'A game that does not count toward the season')],
  //   correctAnswer: 'a',
  //   points: 10
  // },
  // {
  //   id: 'input_40',
  //   question: 'How many points is a touchdown worth in football before any extra point attempt?',
  //   options: [option('a', '3 points'), option('b', '6 points'), option('c', '7 points'), option('d', '2 points')],
  //   correctAnswer: 'b',
  //   points: 10
  // },
  // {
  //   id: 'input_41',
  //   question: 'What makes a college marching band an important part of fall football?',
  //   options: [option('a', 'It replaces the coaching staff'), option('b', 'It determines the final score'), option('c', 'It creates energy and celebrates campus traditions'), option('d', 'It manages ticket sales')],
  //   correctAnswer: 'c',
  //   points: 10
  // },
  // {
  //   id: 'input_23',
  //   question: 'If you could upgrade one campus space, which would you choose?',
  //   options: [option('a', 'A student lounge'), option('b', 'An outdoor gathering area'), option('c', 'A creative or technology lab'), option('d', 'A wellness and study space')],
  //   correctAnswer: null,
  //   points: 20
  // },
  // {
  //   id: 'input_24',
  //   question: 'Finish this sentence: "There\'s no place like ____"',
  //   options: [option('a', 'Home'), option('b', 'My campus'), option('c', 'My school'), option('d', 'All of the above')],
  //   correctAnswer: null,
  //   points: 10
  // },
  // {
  //   id: 'input_25',
  //   question: 'Which deserves its own championship trophy?',
  //   options: [option('a', 'Best school mascot'), option('b', 'Best fight song'), option('c', 'Best game day food'), option('d', 'Best student section')],
  //   correctAnswer: null,
  //   points: 10
  // },
  {
    id: 'input_26',
    question: 'The weather changes right before kickoff. What do you do?',
    options: [option('a', 'Grab a poncho and keep cheering'), option('b', 'Find a covered spot with a great view'), option('c', 'Head to the tailgate until it passes'), option('d', 'Let the group decide the next move')],
    correctAnswer: null,
    points: 10
  },
  {
    id: 'input_27',
    question: 'Retool Your School is designed to help colleges improve what?',
    options: [option('a', 'Conference rankings'), option('b', 'Athletic scholarships'), option('c', 'Football scores'), option('d', 'Campus spaces and environments')],
    correctAnswer: 'd',
    points: 10
  },
  // {
  //   id: 'input_28',
  //   question: 'What is Adelphi University\'s athletic mascot?',
  //   options: [option('a', 'Owls'), option('b', 'Panthers'), option('c', 'Skyhawks'), option('d', 'Fighting Saints')],
  //   correctAnswer: 'b',
  //   points: 10
  // },
  // {
  //   id: 'input_29',
  //   question: 'You see a beautiful new campus space filled with students taking pictures, hanging out and celebrating their school. What RYS goal does this illustrate?',
  //   options: [option('a', 'Recruiting professional athletes'), option('b', 'Changing conference membership'), option('c', 'Creating spaces that foster community'), option('d', 'Increasing football rankings')],
  //   correctAnswer: 'c',
  //   points: 10
  // },
  {
    id: 'input_30',
    question: 'What does "SWAC" stand for?',
    options: [option('a', 'Southwestern Athletic Conference'), option('b', 'Southern Western Athletic Conference'), option('c', 'Southwestern Academic Conference'), option('d', 'Southern World Athletic Conference')],
    correctAnswer: 'a',
    points: 10
  }
  // {
  //   id: 'input_31',
  //   question: 'What do the CIAA, MEAC, SIAC and SWAC have in common with the Retool Your School program?',
  //   options: [option('a', 'They connect colleges and their communities'), option('b', 'They award Retool Your School grants'), option('c', 'They operate Home Depot stores'), option('d', 'They organize the Retool Your School intake forms')],
  //   correctAnswer: 'a',
  //   points: 10
  // },
  // {
  //   id: 'input_32',
  //   question: 'Why can campus spaces matter beyond football season?',
  //   options: [option('a', 'They only matter during home games'), option('b', 'They can create places for students to gather, connect and build community'), option('c', 'They are only used for athletes'), option('d', 'They have no impact on student life')],
  //   correctAnswer: 'b',
  //   points: 10
  // },
  // {
  //   id: 'input_33',
  //   question: 'What kind of schools can participate in the Retool Your School program?',
  //   options: [option('a', 'Only professional sports teams'), option('b', 'Only high schools'), option('c', 'Eligible accredited institutions of higher education'), option('d', 'Only schools with football teams')],
  //   correctAnswer: 'c',
  //   points: 10
  // },
  // {
  //   id: 'input_34',
  //   question: 'Which of these is most likely to bring the Retool Your School spirit to life?',
  //   options: [option('a', 'An unused classroom'), option('b', 'A locked campus building'), option('c', 'An empty parking lot'), option('d', 'A campus space that brings students together')],
  //   correctAnswer: null,
  //   points: 10
  // }
];

export default {
  name: 'QuizDiscover',

  props: {
    icon: { type: String, default: 'xmark' }
  },

  emits: ['hide'],

  data() {
    return {
      QUESTIONS_PER_QUIZ,
      studentsSrc,
      winPointsSrc: WIN_POINTS_IMAGES[0],
      noPointsSrc: NO_POINTS_IMAGES[0],
      resultsSrc,
      currentPage: 'start',
      selectedQuestions: [],
      questionIndex: 0,
      answers: {},
      selectedAnswer: null,
      answerResult: null,
      score: 0,
      email: '',
      emailError: false,
      submitting: false,
      inactivityTimer: null,
      displayedPoints: 0,
      countUpFrame: null,
      showShareOptions: false,
      SHARE_NETWORKS
    };
  },

  computed: {
    currentQuestion() {
      return this.selectedQuestions[this.questionIndex];
    },

    questionNumber() {
      return this.questionIndex + 1;
    },

    isLastQuestion() {
      return this.questionNumber === QUESTIONS_PER_QUIZ;
    },

    maxScore() {
      return this.selectedQuestions.reduce((total, question) => total + question.points, 0);
    }
  },

  beforeUnmount() {
    this.clearInactivityTimer();
    this.stopCountUp();
  },

  methods: {
    shuffle(array) {
      const copy = [...array];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    },

    startQuiz() {
      this.selectedQuestions = this.shuffle(QUESTIONS).slice(0, QUESTIONS_PER_QUIZ);
      this.questionIndex = 0;
      this.answers = {};
      this.selectedAnswer = null;
      this.answerResult = null;
      this.score = 0;
      this.email = '';
      this.emailError = false;
      this.currentPage = 'question';
      this.resetInactivityTimer();
    },

    handleAnswer() {
      if (this.selectedAnswer === null) {
        return;
      }

      this.resetInactivityTimer();
      this.answers[this.currentQuestion.id] = this.selectedAnswer;

      const { correctAnswer, points } = this.currentQuestion;
      if (correctAnswer === null || this.selectedAnswer === correctAnswer) {
        this.score += points;
        this.answerResult = 'correct';
        this.winPointsSrc = WIN_POINTS_IMAGES[Math.floor(Math.random() * WIN_POINTS_IMAGES.length)];
        this.countUpPoints(points);
      }
      else {
        this.noPointsSrc = NO_POINTS_IMAGES[Math.floor(Math.random() * NO_POINTS_IMAGES.length)];
        this.answerResult = 'incorrect';
      }
    },

    countUpPoints(points) {
      this.stopCountUp();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.displayedPoints = points;
        return;
      }

      this.displayedPoints = 0;
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / COUNT_UP_DURATION_MS, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.displayedPoints = Math.round(eased * points);
        this.countUpFrame = progress < 1 ? requestAnimationFrame(step) : null;
      };
      this.countUpFrame = requestAnimationFrame(step);
    },

    stopCountUp() {
      if (this.countUpFrame) {
        cancelAnimationFrame(this.countUpFrame);
        this.countUpFrame = null;
      }
    },

    nextQuestion() {
      this.stopCountUp();
      this.answerResult = null;
      this.selectedAnswer = null;

      if (this.isLastQuestion) {
        this.currentPage = 'email';
        this.resetInactivityTimer();
        return;
      }

      this.questionIndex++;
      this.resetInactivityTimer();
    },

    async submitEmail() {
      if (this.submitting) {
        return;
      }

      if (!EMAIL_PATTERN.test(this.email)) {
        this.emailError = true;
        return;
      }

      this.emailError = false;
      this.submitting = true;
      this.clearInactivityTimer();
      await Promise.all([this.submitForm(), this.submitGoldLasso()]);
      this.submitting = false;
      this.currentPage = 'results';
    },

    async submitForm() {
      try {
        const url = new URL(`/wp-json/gf/v2/forms/${FORM_ID}/submissions`, this.$config.public.rysApiUrl).href;
        const response = await $fetch(url, {
          method: 'POST',
          body: { ...this.answers, [EMAIL_FIELD_ID]: this.email }
        });
        if (response.is_valid !== true) {
          console.error('QuizDiscover submission invalid:', response);
        }
      }
      catch (error) {
        console.error('QuizDiscover submission error:', error);
      }
    },

    async submitGoldLasso() {
      try {
        await $fetch('/api/goldlasso-subscribe', {
          method: 'POST',
          body: { email: this.email }
        });
      }
      catch (error) {
        console.error('QuizDiscover GoldLasso error:', error);
      }
    },

    getShareData() {
      // Uses the current origin so staging/branch deploys share their own function.
      const { origin } = window.location;
      return {
        origin,
        shareUrl: `${origin}${SHARE_PATH}?score=${this.score}&max=${this.maxScore}`,
        text: `I scored ${this.score} out of ${this.maxScore} points on the Retool Your School quiz!`
      };
    },

    onShareClick() {
      // On mobile the networks' apps intercept their share links and drop the URL/text,
      // so skip the icon menu and hand off to the OS share sheet, whose app extensions
      // do take the link.
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (isMobile && navigator.share) {
        const { shareUrl, text } = this.getShareData();
        navigator.share({ url: shareUrl, text }).catch(() => {});
        return;
      }

      this.showShareOptions = !this.showShareOptions;
    },

    shareScore(network) {
      const { origin, shareUrl, text } = this.getShareData();

      const networkUrl = network.buildUrl({
        url: encodeURIComponent(shareUrl),
        text: encodeURIComponent(text),
        image: encodeURIComponent(`${origin}/quiz-share.png`)
      });

      window.open(networkUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
    },

    resetInactivityTimer() {
      this.clearInactivityTimer();
      this.inactivityTimer = setTimeout(() => {
        this.currentPage = 'timeout';
      }, INACTIVITY_TIMEOUT_MS);
    },

    clearInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }
    },

    resetQuiz() {
      this.clearInactivityTimer();
      this.stopCountUp();
      this.currentPage = 'start';
      this.selectedQuestions = [];
      this.questionIndex = 0;
      this.answers = {};
      this.selectedAnswer = null;
      this.answerResult = null;
      this.score = 0;
      this.email = '';
      this.emailError = false;
      this.submitting = false;
      this.showShareOptions = false;
    },

    handleClose() {
      this.resetQuiz();
      this.$emit('hide');
    }
  }
};
</script>

<style lang="scss" scoped>
.contact-form {
  // Fixed height so every quiz step (start, question, result, email, etc.) renders
  // at the same size; content taller than this scrolls inside the popup.
  --quiz-height: 684px;

  width: calc(100vw - 32px);
  height: min(var(--quiz-height), calc(100vh - 32px));
  height: min(var(--quiz-height), calc(100dvh - 32px));
  background: #ffffff;
  text-align: center;
  display: flex;
  flex-direction: column;
  overflow-x: auto;

  @media screen and (min-width: $breakpoint-sm) {
    width: 640px;
    overflow-x: clip;
  }

  // Each step is a direct child; stretch it so its background fills the popup.
  > div {
    flex: 1 0 auto;

    // The question step nests its correct/incorrect result screen one level deeper.
    > .correct-message,
    > .incorrect-message {
      min-height: 100%;
    }
  }

  &.start-page {
    background: $thd-orange;
    padding-bottom: 40px;

    .top {
      display: none;
    }

    h1 {
      font-family: 'Yearbook Solid W01';
      font-size: 3.75em;
      font-weight: 400;
      margin: 0;

      @media screen and (min-width: $breakpoint-sm) {
        font-size: 6.75em;
      }
    }

    h2 {
      color: #fff;
      font-size: 1.5em;
      text-transform: uppercase;
      margin: 0 20px 10px;

      @media screen and (min-width: $breakpoint-sm) {
        font-size: 2.875em;
      }
    }

    p {
      font-size: 1em;
      margin: 0 0 80px;
      padding: 0 10px;

      @media screen and (min-width: $breakpoint-sm) {
        font-size: 1.375em;
        margin: 0 0 40px;
        padding: 0 40px;
      }
    }

    .btn:not(.btn-invert) {
      background: #000000;
      border-color: #000000;
      color: #ffffff;

      &:hover {
        background: #000000;
        border-color: #000000;
        color: #ffffff;
      }
    }

    .btn-invert:hover {
      background: #000000;
      border-color: #000000;
      color: #ffffff;
    }
  }

  .top {
    background: #000000;
    border: none;
    display: flex;
    justify-content: flex-end;
    overflow: hidden;
    position: relative;
    padding: 10px 40px;
    gap: 12px;

    legend {
      color: #ffffff;
      font-size: 18px;
      font-weight: 700;
      line-height: 24px;
      text-align: center;
      width: 100%;
    }

    .top-title {
      align-items: center;
      display: flex;
      gap: 12px;
      justify-content: flex-start;
      width: 100%;

      legend {
        width: auto;
      }
    }

    .points-get {
      display: flex;
      align-items: center;
      background: #FFFFFF33;
      border-radius: 8px;
      padding: 4px 8px;
      margin-right: 20px;

      p {
        color: #fff;
        font-size: 18px;
        font-weight: 700;
        line-height: 24px;
        text-align: center;
        margin: 0;
        padding: 0;
        white-space: nowrap;
      }
    }

    .progress {
      display: flex;
      gap: 4px;
    }

    .progress-box {
      background: #000000;
      border: 2px solid #ffffff;
      height: 12px;
      width: 12px;

      &.active {
        background: $thd-orange;
        border-color: $thd-orange;
      }
    }

    .close-btn {
      background: none;
      border: none;
      color: #ffffff;
      cursor: pointer;
      font-size: 18px;
      margin: 2px 20px 2px 2px;
      position: absolute;
      right: 0;
      top: 14px;
    }
  }

  fieldset {
    padding: 0 15px 40px;
    border: none;

    @media screen and (min-width: $breakpoint-sm) {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 592px;
      padding: 0 40px 40px;
    }

    h2 {
      font-size: 30px;
      font-weight: 700;
      line-height: 30px;
      letter-spacing: 3%;
      text-align: center;
      text-transform: uppercase;
      margin: 40px 0;

      @media screen and (min-width: $breakpoint-sm) {
        font-size: 40px;
        line-height: 40px;
      }
    }

    p {
      padding: 0;
    }

    .answers {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 30px;

      @media screen and (min-width: $breakpoint-sm) {
        margin-bottom: 70px;
      }

      .btn-invert {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        font-size: 20px;
        font-weight: 500;
        border: 1px solid transparent;
        background: transparent;
        border-color: #000000;
        color: #000000;

        &:hover {
          background: #F6D8C4;
          border: 2px solid #f96302;

          @media screen and (min-width: $breakpoint-sm) {
            background: #F6D8C4;
            border: 2px solid #f96302;
          }

          &.has-image img {
            border: 2px solid #f96302;
          }
        }

        &:active {
          background: #F6D8C4;
          border: 2px solid #f96302;
        }

        &.has-image {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          height: auto;

          img {
            object-fit: cover;
            width: 80px;
            height: 80px;
            border: 1px solid #C4C4C4;
            border-radius: 80px;
            margin-top: 0;
          }
        }
      }
    }
  }

  .hero {
    background: #000000;
    display: flex;
    justify-content: center;
    overflow: hidden;
    padding-top: 0;
    position: relative;
    margin-bottom: 20px;

    .hero-text {
      display: flex;
      flex-direction: column;
      inset: 0;
      justify-content: space-evenly;
      position: absolute;
      white-space: nowrap;

      span {
        color: rgb(255 255 255 / 20%);
        font-family: 'Yearbook Solid W01';
        font-size: clamp(28px, 8vw, 70px);
        font-weight: 400;
        line-height: 1;
        text-align: center;
        text-transform: uppercase;
      }
    }

    img {
      display: block;
      height: 185px;
      width: 100%;
      object-fit: cover;
      object-position: top;
      margin-top: 0;
      max-width: 100%;
      position: relative;

      @media screen and (min-width: $breakpoint-sm) {
        height: 286px;
      }
    }
  }

  .hero-result {
    background: $thd-orange;

    .hero-text span {
      font-size: clamp(32px, 10vw, 80px);
    }
  }

  .correct-message {
    background: #000;

    .hero-result {
      background: #f96302;
    }

    h1 {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      color: #fff;
      font-family: 'Yearbook Solid W01';
      font-size: 200px;
      font-weight: 400;
      line-height: 1;
      margin: 0;

      span {
        font-size: 80px;
      }
    }

    h2 {
      color: #fff;
      font-size: 40px;
      line-height: 40px;
      letter-spacing: 3%;
      text-transform: uppercase;
      margin: 10px 0 30px;
    }
  }

  .incorrect-message {
    background: #373737;

    .hero-result {
      background: #000;
    }

    h1 {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      color: #fff;
      font-family: 'Yearbook Solid W01';
      font-size: 200px;
      font-weight: 400;
      line-height: 1;
      margin: 0;

      span {
        font-size: 80px;
      }
    }

    h2 {
      color: #fff;
      font-size: 40px;
      line-height: 40px;
      letter-spacing: 3%;
      text-transform: uppercase;
      margin: 10px 0 30px;
    }
  }

  img {
    margin-top: 40px;
  }

  p {
    font-size: 20px;
    font-weight: 500;
    text-align: center;
    padding: 0 15px;
    margin: 40px 0;

    @media screen and (min-width: $breakpoint-sm) {
      padding: 0 70px;
    }

    &.score {
      font-weight: 700;
    }
  }

  .results-message {
    background: $thd-orange;
    padding-bottom: 40px;

    .hero-result {
      background: #000;
    }

    .close-btn {
      background: none;
      border: none;
      color: #ffffff;
      cursor: pointer;
      font-size: 18px;
      padding: 2px;
      position: absolute;
      right: 20px;
      top: 14px;
      z-index: 1;
    }

    h1 {
      color: #fff;
      font-family: 'Yearbook Solid W01';
      font-size: 200px;
      font-weight: 400;
      line-height: 1;
      margin: 0;
      text-align: center;
    }

    h2 {
      color: #111111;
      font-size: 40px;
      line-height: 40px;
      letter-spacing: 3%;
      text-transform: uppercase;
      margin: 10px 0 0;
      text-align: center;
    }

    .final-score p {
      color: #fff;
      font-size: 24px;
      font-weight: 700;
      line-height: 28px;
      margin: 0 0 30px;
    }

    .btn-outline {
      background: transparent;
      border-color: #000;
      color: #000;

      &:hover {
        background: #000;
        color: #fff;
      }
    }

    .btn-solid {
      background: #000;
      border-color: #000;
      color: #fff;

      &:hover {
        background: transparent;
        color: #000;
      }
    }

    .share-options {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;
      margin: 20px 15px 0;
    }

    .share-option {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border: 2px solid #000000;
      border-radius: 50%;
      background: #000000;
      color: #ffffff;
      font-size: 20px;
      cursor: pointer;

      &:hover,
      &:focus-visible {
        background: transparent;
        color: #000000;
      }
    }
  }

  .email-page {
    background: $thd-orange;
    color: #ffffff;

    .hero-result {
      background: #000000;
    }

    form {
      padding: 0 15px 40px;

      @media screen and (min-width: $breakpoint-sm) {
        padding: 0 40px 40px;
      }
    }

    h2 {
      color: #fff;
      font-family: 'Yearbook Solid W01';
      font-size: 64px;
      font-weight: 400;
      line-height: 100%;
      letter-spacing: 3%;
      text-transform: uppercase;
      margin: 20px 0 0;

      @media screen and (min-width: $breakpoint-sm) {
        font-size: 120px;
      }
    }

    p {
      color: #ffffff;
      font-weight: 700;
      font-size: 22px;
      line-height: 28px;
      letter-spacing: 0%;
      text-align: center;
      margin: 15px 0 40px;
    }

    .input-holder {
      margin-bottom: 40px;

      input {
        border: 1px solid #000000;
        font-size: 20px;
        padding: 14px 16px;
        width: 89%;

        @media screen and (min-width: $breakpoint-sm) {
          width: 94%;
        }

        &:focus {
          border: 2px solid #000000;
          outline: none;
        }

        &.error {
          border: 2px solid #d00000;
        }
      }

      .error-message {
        color: #ffffff;
        font-size: 16px;
        font-weight: 700;
        margin: 10px 0 0;
        padding: 0;
      }
    }

    .btn {
      background: #000000;
      border-color: #000000;
      color: #ffffff;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }

  .visually-hidden {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  .qr-placeholder {
    align-items: center;
    border: 2px dashed $thd-cool-gray;
    color: $thd-cool-gray;
    display: flex;
    height: 200px;
    justify-content: center;
    margin: 0 auto 40px;
    width: 200px;
  }

  .answer {
    display: none;

    &:checked + .btn-invert {
      background: #F6D8C4;
      border: 2px solid #f96302;

      &.has-image img {
        border: 2px solid #F96302;
      }
    }
  }

  .next-button {
    margin-top: 24px;

    .btn:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .btn {
      background: #000;

      &:hover {
        color: #fff;
        border-color: #000;
      }
    }
  }

  .center-button {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    margin: 0 15px;

    @media screen and (min-width: $breakpoint-sm) {
      flex-direction: row;
    }
  }

  .btn-invert {
    font-size: 18px;
    font-weight: 700;
    border: 2px solid transparent;
    background: transparent;
    border-color: #000000;
    color: #000000;

    &:hover {
      background: #ffffff;
      border-color: #000000;
      color: #000000;

      @media screen and (min-width: $breakpoint-sm) {
        background: #f96302;
        border-color: #f96302;
        color: #ffffff;
      }
    }

    &:active {
      background: #f96302;
      border-color: #f96302;
      color: #ffffff;
    }
  }
}
</style>
